/**
 * ScopeOps Security Settings Page
 * SEC-001: SSO Configuration
 * SEC-004-006: MFA Management
 * SEC-007-009: Audit Log Viewer
 * 
 * Created: March 12, 2026
 */

import React, { useState, useEffect } from 'react';
import {
  Shield, Key, Lock, Users, AlertTriangle, FileText,
  Settings, Eye, EyeOff, Copy, Check, RefreshCw,
  Download, ChevronDown, ChevronRight, Search, Filter,
  Bell, XCircle, CheckCircle, Info, Loader2, QrCode
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { toast } from 'sonner';

// =============================================================================
// SSO Configuration Tab
// =============================================================================
const SSOConfigTab = ({ tenantId }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadConfig();
  }, [tenantId]);

  const loadConfig = async () => {
    try {
      const response = await api.get(`/api/security/sso/config/${tenantId}`);
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to load SSO config:', err);
      setConfig({
        tenant_id: tenantId,
        sso_enabled: false,
        provider: 'none'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.post('/api/security/sso/config', config);
      toast.success('SSO configuration saved');
    } catch (err) {
      toast.error('Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      const response = await api.post(`/api/security/sso/test-connection/${tenantId}`);
      setTestResult(response.data);
    } catch (err) {
      toast.error('Connection test failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable SSO */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Single Sign-On (SSO)</h3>
            <p className="text-sm text-gray-500">Configure enterprise authentication</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config?.sso_enabled || false}
              onChange={(e) => setConfig({ ...config, sso_enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>

        {config?.sso_enabled && (
          <>
            {/* Provider Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">SSO Provider</label>
              <select
                value={config?.provider || 'none'}
                onChange={(e) => setConfig({ ...config, provider: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="none">Select Provider</option>
                <option value="saml">SAML 2.0 (Generic)</option>
                <option value="azure_ad">Azure Active Directory</option>
                <option value="okta">Okta</option>
                <option value="google">Google Workspace</option>
              </select>
            </div>

            {/* SAML Configuration */}
            {config?.provider === 'saml' && (
              <div className="space-y-4 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900">SAML 2.0 Configuration</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IdP Metadata URL</label>
                  <input
                    type="url"
                    value={config?.saml_idp_metadata_url || ''}
                    onChange={(e) => setConfig({ ...config, saml_idp_metadata_url: e.target.value })}
                    placeholder="https://idp.example.com/metadata.xml"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IdP Entity ID</label>
                  <input
                    type="text"
                    value={config?.saml_idp_entity_id || ''}
                    onChange={(e) => setConfig({ ...config, saml_idp_entity_id: e.target.value })}
                    placeholder="https://idp.example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IdP SSO URL</label>
                  <input
                    type="url"
                    value={config?.saml_idp_sso_url || ''}
                    onChange={(e) => setConfig({ ...config, saml_idp_sso_url: e.target.value })}
                    placeholder="https://idp.example.com/sso"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IdP Certificate (PEM)</label>
                  <textarea
                    value={config?.saml_idp_certificate || ''}
                    onChange={(e) => setConfig({ ...config, saml_idp_certificate: e.target.value })}
                    rows={4}
                    placeholder="-----BEGIN CERTIFICATE-----..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
                  />
                </div>
              </div>
            )}

            {/* OAuth Configuration (Azure AD, Okta, Google) */}
            {['azure_ad', 'okta', 'google'].includes(config?.provider) && (
              <div className="space-y-4 border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900">OAuth 2.0 / OIDC Configuration</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                  <input
                    type="text"
                    value={config?.oauth_client_id || ''}
                    onChange={(e) => setConfig({ ...config, oauth_client_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                  <div className="relative">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={config?.oauth_client_secret || ''}
                      onChange={(e) => setConfig({ ...config, oauth_client_secret: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {config?.provider === 'azure_ad' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID</label>
                    <input
                      type="text"
                      value={config?.oauth_tenant_id || ''}
                      onChange={(e) => setConfig({ ...config, oauth_tenant_id: e.target.value })}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                )}

                {config?.provider === 'okta' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Okta Domain</label>
                    <input
                      type="text"
                      value={config?.oauth_domain || ''}
                      onChange={(e) => setConfig({ ...config, oauth_domain: e.target.value })}
                      placeholder="your-org.okta.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Advanced Settings */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.enforce_sso || false}
                    onChange={(e) => setConfig({ ...config, enforce_sso: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Enforce SSO (disable local login)</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={config?.allow_local_fallback !== false}
                    onChange={(e) => setConfig({ ...config, allow_local_fallback: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Allow fallback to local auth</span>
                </label>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save Configuration
        </button>

        {config?.sso_enabled && config?.provider !== 'none' && (
          <button
            onClick={testConnection}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Test Connection
          </button>
        )}
      </div>

      {/* Test Results */}
      {testResult && (
        <div className={`p-4 rounded-lg border ${testResult.overall_status === 'pass' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <h4 className="font-medium mb-2">{testResult.overall_status === 'pass' ? 'Connection Successful' : 'Connection Issues'}</h4>
          <ul className="space-y-1 text-sm">
            {testResult.tests?.map((test, idx) => (
              <li key={idx} className="flex items-center gap-2">
                {test.status === 'pass' ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : test.status === 'warning' ? (
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span>{test.name}: {test.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MFA Management Tab
// =============================================================================
const MFATab = ({ tenantId, userId }) => {
  const [policy, setPolicy] = useState(null);
  const [userMFA, setUserMFA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollData, setEnrollData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodesShown, setBackupCodesShown] = useState(false);

  useEffect(() => {
    loadData();
  }, [tenantId, userId]);

  const loadData = async () => {
    try {
      const [policyRes, statusRes] = await Promise.all([
        api.get(`/api/security/mfa/policy/${tenantId}`),
        api.get(`/api/security/mfa/status/${userId}`)
      ]);
      setPolicy(policyRes.data);
      setUserMFA(statusRes.data);
    } catch (err) {
      console.error('Failed to load MFA data:', err);
    } finally {
      setLoading(false);
    }
  };

  const startEnrollment = async () => {
    setEnrolling(true);
    try {
      const response = await api.post('/api/security/mfa/enroll', { user_id: userId });
      setEnrollData(response.data);
      setBackupCodesShown(true);
    } catch (err) {
      toast.error('Failed to start MFA enrollment');
    } finally {
      setEnrolling(false);
    }
  };

  const verifyEnrollment = async () => {
    try {
      await api.post('/api/security/mfa/verify-enrollment', {
        user_id: userId,
        code: verifyCode
      });
      toast.success('MFA enabled successfully!');
      setEnrollData(null);
      setVerifyCode('');
      loadData();
    } catch (err) {
      toast.error('Invalid verification code');
    }
  };

  const disableMFA = async () => {
    const code = prompt('Enter your current MFA code to disable:');
    if (!code) return;

    try {
      await api.post('/api/security/mfa/disable', {
        user_id: userId,
        code: code
      });
      toast.success('MFA disabled');
      loadData();
    } catch (err) {
      toast.error('Failed to disable MFA');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Policy Info */}
      <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900">Organization MFA Policy</h4>
            <p className="text-sm text-blue-700">
              MFA is <strong>{policy?.mfa_status || 'optional'}</strong> for your organization.
              {policy?.mfa_status === 'required' && (
                <span className="block mt-1">
                  You must enable MFA within {policy?.mfa_grace_period_days || 7} days.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* User MFA Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Your MFA Status</h3>
            <p className="text-sm text-gray-500">
              {userMFA?.mfa_enabled ? 'MFA is enabled on your account' : 'MFA is not enabled'}
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            userMFA?.mfa_enabled 
              ? 'bg-green-100 text-green-700' 
              : 'bg-amber-100 text-amber-700'
          }`}>
            {userMFA?.mfa_enabled ? 'Enabled' : 'Not Enabled'}
          </div>
        </div>

        {userMFA?.mfa_enabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>Method: <strong>TOTP (Authenticator App)</strong></span>
              <span>Backup codes remaining: <strong>{userMFA?.backup_codes_remaining || 0}</strong></span>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={disableMFA}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Disable MFA
              </button>
              <button
                onClick={async () => {
                  const code = prompt('Enter your current MFA code:');
                  if (!code) return;
                  try {
                    const response = await api.post('/api/security/mfa/regenerate-backup-codes', {
                      user_id: userId,
                      code: code
                    });
                    setEnrollData(response.data);
                    setBackupCodesShown(true);
                    toast.success('New backup codes generated');
                  } catch (err) {
                    toast.error('Failed to regenerate codes');
                  }
                }}
                className="text-purple-600 hover:text-purple-700 text-sm"
              >
                Regenerate Backup Codes
              </button>
            </div>
          </div>
        ) : enrollData ? (
          // Enrollment Flow
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* QR Code */}
              <div className="text-center">
                <h4 className="font-medium text-gray-900 mb-3">1. Scan QR Code</h4>
                {enrollData.qr_code && (
                  <img 
                    src={enrollData.qr_code} 
                    alt="MFA QR Code" 
                    className="mx-auto border border-gray-200 rounded-lg"
                  />
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Or enter manually: <code className="bg-gray-100 px-1 rounded">{enrollData.secret}</code>
                </p>
              </div>

              {/* Verification */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">2. Enter Verification Code</h4>
                <input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full px-4 py-3 text-2xl text-center tracking-widest border border-gray-300 rounded-lg"
                />
                <button
                  onClick={verifyEnrollment}
                  disabled={verifyCode.length !== 6}
                  className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  Verify & Enable MFA
                </button>
              </div>
            </div>

            {/* Backup Codes */}
            {backupCodesShown && enrollData.backup_codes && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Backup Codes (Save These!)</h4>
                  <button
                    onClick={() => copyToClipboard(enrollData.backup_codes.join('\n'))}
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                  >
                    <Copy className="w-4 h-4" /> Copy All
                  </button>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {enrollData.backup_codes.map((code, idx) => (
                    <code key={idx} className="bg-gray-100 px-2 py-1 rounded text-center text-sm font-mono">
                      {code}
                    </code>
                  ))}
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Store these codes safely. Each can only be used once.
                </p>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={startEnrollment}
            disabled={enrolling}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
            Enable MFA
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Audit Log Viewer Tab
// =============================================================================
const AuditLogTab = ({ tenantId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    user_email: '',
    success: '',
    page: 1
  });
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    loadLogs();
  }, [tenantId, filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tenantId) params.append('tenant_id', tenantId);
      if (filters.action) params.append('action', filters.action);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.success !== '') params.append('success', filters.success);
      params.append('page', filters.page);
      params.append('limit', '50');

      const response = await api.get(`/api/security/audit/logs?${params}`);
      setLogs(response.data.audit_logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = async () => {
    try {
      const response = await api.get(`/api/security/audit/logs/export?tenant_id=${tenantId}&format=csv`);
      // Download CSV
      const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Audit logs exported');
    } catch (err) {
      toast.error('Failed to export logs');
    }
  };

  const actionLabels = {
    login: 'Login',
    logout: 'Logout',
    login_failed: 'Failed Login',
    mfa_enroll: 'MFA Enrollment',
    mfa_verify: 'MFA Verification',
    password_change: 'Password Change',
    permission_change: 'Permission Change',
    config_change: 'Config Change',
    export: 'Data Export',
    sso_config: 'SSO Configuration'
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              value={filters.user_email}
              onChange={(e) => setFilters({ ...filters, user_email: e.target.value, page: 1 })}
              placeholder="Search by email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Actions</option>
            {Object.entries(actionLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={filters.success}
            onChange={(e) => setFilters({ ...filters, success: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Results</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
          <button
            onClick={exportLogs}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No audit logs found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Action</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">User</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">IP Address</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500">Result</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        {actionLabels[log.action] || log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {log.user_email || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {log.ip_address || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600 mx-auto" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setExpanded({ ...expanded, [log.id]: !expanded[log.id] })}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {expanded[log.id] ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                  {expanded[log.id] && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3 bg-gray-50">
                        <pre className="text-xs text-gray-600 overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Showing {((filters.page - 1) * 50) + 1} - {Math.min(filters.page * 50, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page * 50 >= total}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Main Security Settings Page
// =============================================================================
const SecuritySettings = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('sso');
  
  // TENANT ISOLATION: Use standardized hook for correct tenant ID
  const { tenantId } = useActiveTenant();
  const userId = user?.id;

  const tabs = [
    { id: 'sso', label: 'Single Sign-On', icon: Key },
    { id: 'mfa', label: 'Multi-Factor Auth', icon: Lock },
    { id: 'audit', label: 'Audit Logs', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="security-settings-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-purple-100 rounded-xl">
            <Shield className="w-6 h-6 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Security Settings</h1>
        </div>
        <p className="text-gray-500">Configure authentication, MFA, and view security audit logs</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {activeTab === 'sso' && <SSOConfigTab tenantId={tenantId} />}
        {activeTab === 'mfa' && <MFATab tenantId={tenantId} userId={userId} />}
        {activeTab === 'audit' && <AuditLogTab tenantId={tenantId} />}
      </div>
    </div>
  );
};

export default SecuritySettings;
